import React from 'react';
import { IBanner } from '../MobileAppTypes';
import Button from '../../_Core/Button';
import { FormatDate, ToDate } from '../../../utils/Tools';

interface IBannerListProps {
  banners: IBanner[];
  isLoading?: boolean;
  onRefresh: () => void;
  onNew: () => void;
  onEdit: (banner: IBanner) => void;
  onRemove: (banner: IBanner) => void;
}

const getBannerStatus = (banner: IBanner): { label: string; color: string } => {
  const now = new Date();
  const start = banner.DisplayStartAtUtc ? ToDate(banner.DisplayStartAtUtc) : undefined;
  const end = banner.DisplayEndAtUtc ? ToDate(banner.DisplayEndAtUtc) : undefined;
  if (start && start > now) {
    return { label: 'Scheduled', color: '#5bc0de' };
  }
  if (end && end < now) {
    return { label: 'Expired', color: '#999' };
  }
  if (!banner.DisplayStartAtUtc && !banner.DisplayEndAtUtc) {
    return { label: 'Sticky', color: '#8e44ad' };
  }
  return { label: 'Active', color: '#5cb85c' };
};

const BannerList: React.FC<IBannerListProps> = ({ banners, isLoading, onRefresh, onNew, onEdit, onRemove }) => {
  const sorted = [...banners].sort((a, b) => {
    const dateA = a.CreatedAtUtc ? new Date(a.CreatedAtUtc).getTime() : 0;
    const dateB = b.CreatedAtUtc ? new Date(b.CreatedAtUtc).getTime() : 0;
    return dateB - dateA;
  });

  return (
    <div>
      <div className="flex-between">
        <h3>Banners ({banners.length})</h3>
        <div className="btn-group">
          <Button type="refresh" onClick={onRefresh} disabled={isLoading} text="Refresh" />
          <Button type="new" onClick={onNew} text="New" />
        </div>
      </div>
      <hr />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <span className="loading-spinner"></span>
          <div style={{ marginTop: '16px' }}>Loading banners...</div>
        </div>
      ) : sorted.length === 0 ? (
        <div className="alert alert-info">No banners yet. Create one to announce something across the app.</div>
      ) : (
        <div className="item-list">
          {sorted.map((banner) => {
            const status = getBannerStatus(banner);
            return (
              <div
                key={banner.PublicId}
                className="list-item"
                style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '15px', margin: '10px 0', backgroundColor: '#fff' }}>
                <div className="flex-between">
                  <div style={{ flex: 1 }}>
                    <div className="flex" style={{ alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                      <span className="badge" style={{ backgroundColor: status.color, color: 'white', fontSize: '0.7em' }}>
                        {status.label}
                      </span>
                      {banner.IsDismissible && (
                        <span className="badge" style={{ backgroundColor: '#f0ad4e', color: 'white', fontSize: '0.7em' }}>
                          Dismissible
                        </span>
                      )}
                      <span className="badge" style={{ backgroundColor: '#5bc0de', color: 'white', fontSize: '0.7em' }}>
                        {banner.AppliesToAllScreens ? 'All screens' : `${banner.TargetScreenPublicIds?.length || 0} screen(s)`}
                      </span>
                    </div>
                    <div>{banner.Message}</div>
                    <div className="small text-muted" style={{ marginTop: '5px' }}>
                      {banner.DisplayStartAtUtc || banner.DisplayEndAtUtc
                        ? `${banner.DisplayStartAtUtc ? `From ${FormatDate(banner.DisplayStartAtUtc, 'pretty')}` : 'No start limit'} — ${
                            banner.DisplayEndAtUtc ? `Until ${FormatDate(banner.DisplayEndAtUtc, 'pretty')}` : 'No end limit'
                          }`
                        : 'Shows indefinitely'}
                    </div>
                  </div>
                  <div className="btn-group" style={{ gap: '5px' }}>
                    <Button type="edit" text="" iconOnly extraSmall onClick={() => onEdit(banner)} />
                    <Button type="remove" text="" iconOnly extraSmall onClick={() => onRemove(banner)} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BannerList;
